// ENG-2277 (HTML Forms): in a Form a signature merge tag goes in a fixed-size
// box with a line under it. The box never changes size, so the page is laid out the same signed and
// unsigned, and the PDF draws it at exactly this size (form-print.utils.ts, adjustBoxes).
import type { Editor } from "tinymce";

import {
  BOX_ATTRIBUTE,
  BOX_LINE_ATTRIBUTE,
  readBoxSize
} from "components/pdf-generation-v3/form-print.utils";

import { MERGE_TAG_REPLACED_VALUE_PREFIX } from "./merge-tags/merge-tags-constants";
import { CURRENT_USER_OPTIONS } from "./merge-tags/merge-tags-options/current-user.utils";
import { PATIENT_MISCELLANEOUS_OPTIONS } from "./merge-tags/merge-tags-options/patient-info/patient-miscellaneous.utils";

export type BoxSizeInches = { width: number; height: number };

export const SIGNATURE_BOX_DEFAULT_SIZE_IN: BoxSizeInches = {
  width: 3,
  height: 0.55
};

const MIN_WIDTH_IN = 0.5;
const MIN_HEIGHT_IN = 0.25;
const MAX_HEIGHT_IN = 3;

const SIGNATURE_BOX_SELECTOR = `[${BOX_ATTRIBUTE}="signature"]`;
const SIZE_BUTTON = "signatureboxsize";

const bareTagName = (tagName: string) =>
  tagName.replace(/^\{\{/, "").replace(/\}\}$/, "").split("(")[0].trim();

// Every other signature tag (appointment, invoice and preferred provider) is a provider's.
const SIGNATURE_BOX_LABELS: Record<string, string> = {
  [PATIENT_MISCELLANEOUS_OPTIONS.SIGNATURE]: "Patient signature",
  [CURRENT_USER_OPTIONS.SIGNATURE]: "Staff signature"
};

export const signatureBoxLabel = (tagName: string) =>
  SIGNATURE_BOX_LABELS[bareTagName(tagName)] ?? "Provider signature";

/**
 * The box for a signature tag, holding `content`: the tag itself while authoring a template, or its
 * resolved value (the sign button, or the signature image) in a document. The label sits under the
 * box, outside it, so it never takes signature space.
 */
export const signatureBoxHtml = (
  tagName: string,
  content: string,
  { width, height }: BoxSizeInches = SIGNATURE_BOX_DEFAULT_SIZE_IN,
  // A converted form keeps the original's label and links the box to its field (for review).
  { label, fieldId }: { label?: string; fieldId?: string } = {}
) =>
  `<div ${BOX_ATTRIBUTE}="signature" ${BOX_LINE_ATTRIBUTE}="true" contenteditable="false"${
    fieldId ? ` data-field="${fieldId}"` : ""
  } style="width: ${width}in; height: ${height}in">${content}</div>` +
  `<div style="font-size: 9pt; color: #444444">${label ?? signatureBoxLabel(tagName)}</div>`;

/**
 * What inserting a signature tag puts in the editor: its box, holding the tag while authoring a
 * template (values off) or the tag's resolved value in a document. A signature with no value on
 * file leaves the box empty, which still prints as the box and its line.
 */
export const boxedSignatureInsert = (
  tagName: string,
  getMergeTagValue: (mergeTag: string) => string | undefined | null,
  mergeTagsValuesEnabled: boolean
) => {
  if (!mergeTagsValuesEnabled) return signatureBoxHtml(tagName, tagName);
  const value = getMergeTagValue(tagName) ?? "";
  const resolved = value.startsWith(MERGE_TAG_REPLACED_VALUE_PREFIX)
    ? value.slice(MERGE_TAG_REPLACED_VALUE_PREFIX.length)
    : value;
  return signatureBoxHtml(tagName, resolved);
};

export const isInSignatureBox = (node: Element) =>
  Boolean(node.closest(SIGNATURE_BOX_SELECTOR));

/**
 * Why the size cannot be used, or null when it can. Without a width to fit in (the field is no
 * longer in Form mode), only the smallest width is checked.
 */
export const validateBoxSize = (
  { width, height }: BoxSizeInches,
  maxWidthIn: number | undefined
) => {
  if (maxWidthIn === undefined && !(width >= MIN_WIDTH_IN)) {
    return `Width must be at least ${MIN_WIDTH_IN} inches.`;
  }
  if (
    maxWidthIn !== undefined &&
    !(width >= MIN_WIDTH_IN && width <= maxWidthIn)
  ) {
    return `Width must be between ${MIN_WIDTH_IN} and ${maxWidthIn} inches.`;
  }
  if (!(height >= MIN_HEIGHT_IN && height <= MAX_HEIGHT_IN)) {
    return `Height must be between ${MIN_HEIGHT_IN} and ${MAX_HEIGHT_IN} inches.`;
  }
  return null;
};

export const setBoxSize = (
  box: HTMLElement,
  { width, height }: BoxSizeInches
) => {
  box.style.width = `${width}in`;
  box.style.height = `${height}in`;
  // TinyMCE serializes an element's style from its data-mce-style copy, so keep the two in step.
  if (box.hasAttribute("data-mce-style")) {
    box.setAttribute("data-mce-style", box.getAttribute("style") ?? "");
  }
};

const boxAtCursor = (editor: Editor) =>
  editor.dom.getParent(
    editor.selection.getNode(),
    SIGNATURE_BOX_SELECTOR
  ) as HTMLElement | null;

export type SignatureBoxOptions = {
  // The width between the page's margins, in inches. Read each time a size is checked: the control
  // is registered once, when the editor starts, and the margins can change after that.
  getMaxWidthIn: () => number | undefined;
};

/** A "Size…" control on a signature box, for its width and height in inches. */
export const addSignatureBoxToolbar = (
  editor: Editor,
  { getMaxWidthIn }: SignatureBoxOptions
) => {
  editor.ui.registry.addButton(SIZE_BUTTON, {
    text: "Size…",
    tooltip: "Signature box size (inches)",
    onAction: () => {
      const box = boxAtCursor(editor);
      if (!box) return;
      const current =
        readBoxSize(box.getAttribute("style")) ?? SIGNATURE_BOX_DEFAULT_SIZE_IN;

      editor.windowManager.open({
        title: "Signature box size",
        body: {
          type: "panel",
          items: [
            { type: "input", name: "width", label: "Width (inches)" },
            { type: "input", name: "height", label: "Height (inches)" }
          ]
        },
        initialData: {
          width: String(current.width),
          height: String(current.height)
        },
        buttons: [
          { type: "cancel", text: "Cancel" },
          { type: "submit", text: "Save", primary: true }
        ],
        onSubmit: (api) => {
          const data = api.getData() as Record<string, string>;
          const size = {
            width: parseFloat(data.width),
            height: parseFloat(data.height)
          };
          const problem = validateBoxSize(size, getMaxWidthIn());
          if (problem) {
            editor.windowManager.alert(problem);
            return;
          }
          editor.undoManager.transact(() => setBoxSize(box, size));
          editor.nodeChanged();
          api.close();
        }
      });
    }
  });

  editor.ui.registry.addContextToolbar("signatureBoxToolbar", {
    predicate: (node) => isInSignatureBox(node),
    items: SIZE_BUTTON,
    position: "node",
    scope: "node"
  });
};
